import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import clsx from 'clsx';

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  path: string;
  icon: string;
  permission?: string;
}

// Navigation organized by category for Org Admin (Command Center)
const navigation: NavItem[] = [
  // Overview
  { name: 'Dashboard', path: '/admin', icon: '📊' },

  // Organization Management
  { name: 'Employees', path: '/admin/employees', icon: '👥', permission: 'ownership:read' },
  { name: 'Org Chart', path: '/admin/org-chart', icon: '🌳', permission: 'ownership:read' },

  // Business Entities
  { name: 'Customers', path: '/admin/customers', icon: '🏢', permission: 'ownership:read' },
  { name: 'Products', path: '/admin/products', icon: '📦', permission: 'ownership:read' },

  // Reporting (inbound → lock → outbound)
  { name: 'Submission Monitor', path: '/admin/submissions', icon: '📥', permission: 'reports:read' },
  { name: 'Manager Lock Status', path: '/admin/rollup-locks', icon: '🔒', permission: 'reports:read' },
  { name: 'Delivery Monitor', path: '/admin/rollups', icon: '📈', permission: 'reports:read' },

  // Tools
  { name: 'DevTools', path: '/admin/simulator', icon: '🧪', permission: 'simulator:use' },
  { name: 'Import Data', path: '/admin/import', icon: '📤', permission: 'ownership:create' },

  // Compliance & Security
  { name: 'Audit Trail', path: '/admin/audit', icon: '🔍', permission: 'audit:read' },

  // Settings
  { name: 'Settings', path: '/admin/settings', icon: '⚙️', permission: 'settings:read' },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const filteredNav = navigation.filter(
    (item) => !item.permission || hasPermission(item.permission.split(':')[0], item.permission.split(':')[1])
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-gray-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-gray-800">
          <Link to="/admin" className="flex items-center gap-2">
            <span className="text-2xl">⚡</span>
            <div>
              <div className="font-bold text-lg">PowerMove</div>
              <div className="text-xs text-gray-400">Command Center</div>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-1">
            {filteredNav.map((item) => {
              const isActive = location.pathname === item.path ||
                (item.path !== '/admin' && location.pathname.startsWith(item.path));
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={clsx(
                      'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    )}
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span className="text-sm font-medium">{item.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
              {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {user?.first_name ? `${user.first_name} ${user.last_name || ''}` : user?.email}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {user?.roles?.[0]?.display_name || 'Admin'}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Back to Main App */}
        <div className="p-4 border-t border-gray-800">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <span>←</span>
            <span>Back to Main App</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
